import { AxiosError } from 'axios';

import { unhandledRequest } from 'api/request';
import { MultiLanguageTextProps } from 'types/languages';

export type SmsDataResponse = {
  sendSms: boolean;
  smsTemplate: string;
};

export type FormResponse = {
  name: string;
  description: string;
  title: string;
  sendSms: boolean;
  smsTemplate: string;
  applicationData: ApplicationData | null;
  templates: Template[];
  applicationForm?: ApplicationForm;
};

type ApplicationData = {
  email: string;
  phone: string;
  template: string;
  preSaved?: boolean;
};

export type Template = {
  id: number;
  title: string;
};

export type ApplicationForm = {
  id: number;
  form: RenderFormProps[];
  sentToEmail: string;
  sentToPhone: string;
  templateId: number | null;
  senderName: string;
  dateCreated: Date;
  completed: boolean;
  dateCompleted: Date;
  url: string;
};

export type RenderFormProps = {
  h3: MultiLanguageTextProps;
  answers: FormAnswer[];
};

type BaseFormAnswer = {
  label: MultiLanguageTextProps;
  value: string;
  type: 'dateField' | 'formField' | 'dropdown' | 'radioGroup' | 'selectMulti';
};

type FileFormAnswer = {
  label: MultiLanguageTextProps;
  value?: FileProps;
  type: 'file';
};

type FileMultiFormAnswer = {
  label: MultiLanguageTextProps;
  value?: FileProps[];
  type: 'file-multi';
};

type FormAnswer = BaseFormAnswer | FileFormAnswer | FileMultiFormAnswer;

export type FileProps = {
  fileName: string;
  valueOriginal: string;
  value: string | null;
};

export type TriggerlogData = {
  save: boolean;
  retrigger?: boolean;
  email: string;
  phone?: string;
  template: string;
  applicationId: string;
};

export type Recipient = {
  email: string;
  name: string;
  id: number;
  triggerId: number;
};

export type EnabledData = {
  enabled: boolean;
};

export type CompletionTemplateTypes =
  | 'integrations_rookies_completion_notify'
  | 'integrations_rookies_completion_applicant';

export type CompletionTemplateData = {
  notifyTemplate: CompletionTemplate | null;
  applicantTemplate: CompletionTemplate | null;
};

export type CompletionTemplate = {
  id: number;
  contentHtml: string;
  subject: string;
  templateType: string;
};

export type SubmitError = AxiosError<{ error: string }>;

export async function onboardingFormGetSms() {
  const response = await unhandledRequest.get<SmsDataResponse>({
    url: `/integrations/rookie/sms-settings-data`,
  });
  return response.data;
}

export async function onboardingFormSetSms(template: string, sendSms: boolean) {
  const response = await unhandledRequest.post({
    url: `/integrations/rookie/sms-settings-data/`,
    data: { template, sendSms },
  });
  return response.data;
}

export async function onboardingFormPageData(applicationId: string) {
  const response = await unhandledRequest.get<FormResponse>({
    url: `/integrations/rookie/${applicationId}/form/`,
  });
  return response.data;
}

export async function sendOnboardingForm(applicationId: string, triggerLogData: TriggerlogData) {
  const response = await unhandledRequest.post({
    url: `/integrations/rookie/${applicationId}/form/`,
    data: { triggerLogData },
  });
  return response.data;
}

export async function onboardingFormEmails() {
  const response = await unhandledRequest.get<Recipient[]>({
    url: '/integrations/rookie/recipient/',
  });

  return response.data;
}

export async function onboardingFormSubmitEmail(name: string, email: string) {
  const response = await unhandledRequest.post({
    url: '/integrations/rookie/recipient/',
    data: { name, email },
  });
  return response.data;
}

export async function onboardingFormRemoveEmail(recipientId: number) {
  const response = await unhandledRequest.delete({
    url: `/integrations/rookie/recipient/${recipientId}/`,
  });
  return response.data;
}
export async function onboardingFormEmailEdit(recipientId: number, name: string, email: string) {
  const response = await unhandledRequest.patch({
    url: `/integrations/rookie/recipient/${recipientId}/`,
    data: { name: name, email: email },
  });
  return response.data;
}

export async function onboardingFormEnabled() {
  const response = await unhandledRequest.get<EnabledData>({
    url: '/integrations/rookie/onboarding-form/',
  });
  return response.data;
}

export async function completionTemplates() {
  const response = await unhandledRequest.get<CompletionTemplateData>({
    url: '/integrations/rookie/completion-templates/',
  });
  return response.data;
}

export async function createCompletionTemplate(
  templateType: string,
  subject: string,
  contentHtml: string,
) {
  const response = await unhandledRequest.post({
    url: '/integrations/rookie/completion-templates/',
    data: { templateType, subject, contentHtml },
  });
  return response.data;
}

export async function updateCompletionTemplate(
  templateId: number,
  subject: string,
  contentHtml: string,
) {
  const response = await unhandledRequest.patch({
    url: `/integrations/rookie/completion-templates/${templateId}/`,
    data: { subject, contentHtml },
  });
  return response.data;
}

export async function removeCompletionTemplate(templateId: number) {
  const response = await unhandledRequest.delete({
    url: `/integrations/rookie/completion-templates/${templateId}/`,
    data: { templateId },
  });
  return response.data;
}
